import React from 'react';
import { Box, Flex, Heading, Icon, Text, VStack, Badge, HStack } from '@chakra-ui/react';
import { AiOutlineTool, AiOutlineClockCircle, AiOutlineRocket } from 'react-icons/ai';
import { motion } from 'framer-motion';

/**
 * Tela PADRÃO de manutenção / planos futuros — usar em qualquer aba ou página
 * que ainda não está disponível para o usuário.
 *
 * Props:
 *   titulo    → título da tela (ex.: "Metas Pessoais")
 *   descricao → texto explicativo opcional
 *   recursos  → lista opcional de { icon, title, desc } com os planos futuros
 */
const EmManutencao = ({
    titulo = 'Tela em manutenção',
    descricao = 'Esta funcionalidade está em desenvolvimento e estará disponível em breve.',
    recursos = [],
}) => {
    return (
        <Flex
            direction="column"
            align="center"
            justify="center"
            minH="50vh"
            bg="surface"
            border="1px solid"
            borderColor="borderPrimary"
            borderRadius="2xl"
            position="relative"
            overflow="hidden"
            py={12}
            px={6}
        >
            {/* Gradientes decorativos de fundo */}
            <Box
                position="absolute"
                top="-100px"
                right="-100px"
                w="300px"
                h="300px"
                bg="radial-gradient(circle, rgba(1,233,117,0.08) 0%, transparent 70%)"
                pointerEvents="none"
            />
            <Box
                position="absolute"
                bottom="-80px"
                left="-80px"
                w="250px"
                h="250px"
                bg="radial-gradient(circle, rgba(66,153,225,0.06) 0%, transparent 70%)"
                pointerEvents="none"
            />

            <VStack spacing={6} position="relative" textAlign="center">
                {/* Ícone animado */}
                <Box
                    as={motion.div}
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                >
                    <Flex
                        w="90px"
                        h="90px"
                        borderRadius="2xl"
                        bg="linear-gradient(135deg, rgba(1,233,117,0.15) 0%, rgba(66,153,225,0.15) 100%)"
                        border="2px solid"
                        borderColor="rgba(1,233,117,0.2)"
                        align="center"
                        justify="center"
                        boxShadow="0 0 40px rgba(1,233,117,0.1)"
                    >
                        <Icon as={AiOutlineTool} color="secondary" boxSize="42px" />
                    </Flex>
                </Box>

                <VStack spacing={2}>
                    <Badge
                        bg="secondarySubtle"
                        color="secondary"
                        fontSize="10px"
                        fontWeight="800"
                        textTransform="uppercase"
                        letterSpacing="1px"
                        px={3}
                        py={1}
                        borderRadius="full"
                    >
                        <HStack spacing={1}>
                            <Icon as={AiOutlineClockCircle} boxSize="11px" />
                            <Text>Em breve</Text>
                        </HStack>
                    </Badge>
                    <Heading size="lg" color="textPrimary" fontWeight="900">
                        {titulo}
                    </Heading>
                    <Text color="textSecondary" fontSize="md" maxW="420px">
                        {descricao}
                    </Text>
                </VStack>

                {/* Cards de planos futuros */}
                {recursos.length > 0 && (
                    <Flex gap={4} mt={2} wrap="wrap" justify="center">
                        {recursos.map((item, i) => (
                            <Box
                                key={i}
                                bg="surfaceRaised"
                                border="1px solid"
                                borderColor="borderPrimary"
                                borderRadius="xl"
                                p={4}
                                w="150px"
                                textAlign="center"
                                opacity={0.75}
                            >
                                <Icon as={item.icon || AiOutlineRocket} color="textMuted" boxSize="22px" mb={2} />
                                <Text fontSize="sm" fontWeight="700" color="textSecondary">
                                    {item.title}
                                </Text>
                                <Text fontSize="10px" color="textMuted">
                                    {item.desc}
                                </Text>
                            </Box>
                        ))}
                    </Flex>
                )}
            </VStack>
        </Flex>
    );
};

export default EmManutencao;
